import { fixedDecimals } from '../../platform/fixedDecimals'
import { createPrng } from './prng'
import { SVG, toSvgDataUri } from './svgDataUri'

/** Grid pitch, in both axes. Exported so tests derive it rather than retype 18. */
export const CELL = 18

/**
 * Chosen for the tile it produces, not inherited.
 *
 * The seed is arbitrary to the generator but not to the artefact: `AppTexture`
 * tiles this 252×125 mesh with `background-repeat`, so its 98 cells are the
 * whole of what a user ever sees and their shares never average toward
 * nominal. The previous default of 1337 drew 36.7% outlined and left 26.5%
 * empty against the design system's 30% and ~37% — visibly denser than a
 * scatter.
 *
 * 1087 lands every share within half a point of §0.2 (29.6 / 16.3 / 10.2 / 7.1
 * / 36.7).
 *
 * A matching tile IS rare, and the earlier note here said the opposite. That
 * 18,779 of the first 20,000 seeds beat 1337 measures how bad 1337 was, not how
 * ordinary 1087 is: by the band the test actually enforces — every share within
 * 1.5 points — only 232 of those 20,000 qualify, about 1.2%. The outlined
 * share's sampling s.d. over 98 cells is ~4.6 points, so a typical seed misses
 * badly.
 *
 * Which matters if you change `CELL`, the four thresholds or the PRNG: the
 * pinned census in hexMesh.test.ts will fail, and the fix is to search for a
 * new seed, NOT to loosen the test or nudge a knob until it passes. Run
 * `node scripts/mesh-seed-census.mjs`.
 *
 * Whole number and inside the 32-bit domain by contract — see the seed check
 * in `buildHexMeshDataUri`.
 */
const DEFAULT_SEED = 1087

function hexPoints(cx: number, cy: number, r: number): string {
  return Array.from({ length: 6 }, (_, i) => {
    const a = (Math.PI / 3) * i - Math.PI / 6
    return `${fixedDecimals(cx + r * Math.cos(a), 2)},${fixedDecimals(cy + r * Math.sin(a), 2)}`
  }).join(' ')
}

/**
 * `ink` is required and has no default — deliberately.
 *
 * Two reasons. A custom property inside a `data:` URI never resolves, so
 * `var(--hex-ink, …)` would render its fallback in both themes and the mesh
 * would carry light ink in dark mode. And any literal default here would be a
 * colour value in JS, which D2 bans and the Task 2 test now catches.
 *
 * The caller reads `--hex-ink` from computed style and rebuilds when
 * `data-theme` flips. M0-R23 still holds: the seed is fixed, so geometry is
 * pixel-identical across re-renders and only the ink changes.
 *
 * The seed default is a whole number on purpose. `createPrng` coerces with
 * `seed >>> 0` rather than validating, so a fractional seed aliases onto its
 * floor with nothing raised — and this is that function's only consumer.
 */
export function buildHexMeshDataUri(
  opts: { ink: string; width?: number; height?: number; seed?: number },
): string {
  const width = opts.width ?? 252
  const height = opts.height ?? 125
  const seed = opts.seed ?? DEFAULT_SEED
  // Reject rather than coerce. `createPrng` does `seed >>> 0`, which turns 1.5
  // into 1 and NaN into 0 with nothing raised — two layers asked for under
  // different seeds would render identically. Being that function's only
  // consumer, this is the one place the prose contract can become a gate, and
  // no test downstream can catch the aliasing by observation because the
  // aliased output is byte-identical to the seed it aliases onto.
  //
  // The range half is not decoration. `Number.isInteger` alone still admits
  // every integer that `>>> 0` folds back into the domain — `2 ** 32` becomes
  // 0 and `-7` becomes 4294967289 — which is the same silent collision the
  // fractional case is rejected for. Caught in review after the first version
  // of this gate shipped with only the integer half. See #73, which proposes
  // moving the whole check onto `createPrng` where the contract is stated.
  if (!Number.isInteger(seed) || seed < 0 || seed > 0xffffffff) {
    throw new Error(
      `hex mesh seed must be a whole number in [0, 2^32), got ${seed}. ` +
        'createPrng coerces with >>> 0, so anything else silently aliases ' +
        'onto a seed another layer could ask for by its own name.',
    )
  }
  const rand = createPrng(seed)
  const INK = opts.ink

  const svg = document.createElementNS(SVG, 'svg')
  // NO setAttribute('xmlns', SVG) — see Task 6's as-delivered note (#21). The
  // element is already in that namespace; setting the attribute too duplicates
  // it in the serialised output and the SVG fails to parse.
  svg.setAttribute('width', String(width))
  svg.setAttribute('height', String(height))

  for (let y = CELL / 2; y < height; y += CELL) {
    for (let x = CELL / 2; x < width; x += CELL) {
      const roll = rand()
      const r = CELL * 0.42
      if (roll < 0.30) {
        const p = document.createElementNS(SVG, 'polygon')
        p.setAttribute('points', hexPoints(x, y, r))
        p.setAttribute('fill', 'none')
        p.setAttribute('stroke', INK)
        p.setAttribute('stroke-width', '0.6')
        svg.append(p)
      } else if (roll < 0.46) {
        const p = document.createElementNS(SVG, 'polygon')
        p.setAttribute('points', hexPoints(x, y, r))
        p.setAttribute('fill', INK)
        p.setAttribute('fill-opacity', '0.35')
        svg.append(p)
      } else if (roll < 0.56) {
        const l = document.createElementNS(SVG, 'line')
        l.setAttribute('x1', fixedDecimals(x - r, 2))
        l.setAttribute('y1', fixedDecimals(y, 2))
        l.setAttribute('x2', fixedDecimals(x - r / 2, 2))
        l.setAttribute('y2', fixedDecimals(y, 2))
        l.setAttribute('stroke', INK)
        l.setAttribute('stroke-width', '0.6')
        svg.append(l)
      } else if (roll < 0.63) {
        const c = document.createElementNS(SVG, 'circle')
        c.setAttribute('cx', fixedDecimals(x, 2))
        c.setAttribute('cy', fixedDecimals(y, 2))
        c.setAttribute('r', '0.9')
        c.setAttribute('fill', INK)
        svg.append(c)
      }
    }
  }

  return toSvgDataUri(svg)
}
